import React, { Component } from 'react';
import { Icon, Modal, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import noop from 'lodash/noop';
import {
  getError,
  required,
  minLength,
  requiredByMinValue,
  scanForInvalidVariables,
  doesHtmlContainAmp,
  getHtmlValidationResult,
} from 'services/forms';
import {
  Input,
  TextArea,
  Drawer,
  Tabs,
  TabPane,
  Button,
  Select,
  SelectOption,
  Row,
  Col,
  Checkbox,
} from 'components/ui';
import './style.css';
import { SEND_THROUGH_LIST } from 'constants/common';
import RateLimitCheckBox from './RateLimitCheckbox';
import BypassUnsubscriptionCheckbox from './BypassUnsubscriptionCheckbox';
import InvalidTemplateVariableWarning from './InvalidTemplateVariableWarning';
import loadable from '@loadable/component';
import { html as beautifyHtml } from 'js-beautify';
import SelectTemplateType from './SelectTemplateType';
import { EMAIL_TYPE_IDS } from 'constants/mail';
import queryClient from '../../../../services/queryClient';
import { getTemplateById } from '../../../../services/templates';
import { getEnv } from 'services/env-service';
import TemplateErrorModal from './TemplateErrorModal';
import PrimaryCTALinkSelector from './PrimaryCTALinkSelector';
import RichTextEditor from 'components/ui/RichTextEditor';
import { getMicrosoftCtaPreview } from './getHtmlPreview';

function minify(s) {
  return s
    .replace(/>[\r\n ]+</g, '><')
    .replace(/(<.*?>)|\s+/g, (m, $1) => ($1 ? $1 : ' '))
    .trim();
}

const CodeEditor = loadable(() => import('components/ui/CodeEditor'));
const RenderHTML = loadable(() =>
  import('components/blocks').then(m => m.RenderHTML),
);

const validators = {
  name: [required, minLength(2)],
  subject: [required, minLength(3)],
  htmlContent: [required, minLength(6) /**, isHtmlValid*/],
  selectedCategoryId: [requiredByMinValue(0)],
};

class EmailEditor extends Component {
  state = {
    name: '',
    category: 'default',
    forceChoose: '',
    subject: '',
    activeTabKey: 'htmlEditor',
    htmlContent: '',
    microsoftHtml: '',
    ampHtml: '',
    textContent: '',
    errors: {},
    selectedCategoryId: -1,
    selectedEmailTypeId: -1,
    ampEnabled: false,
    microsoftEnabled: false,
    isRateLimitExcluded: false,
    bypassUnsubscription: false,
    ampReplaceMagicLink: false,
    linterErrors: {},
    template: null,
    misspellingWords: [],
    ampMisspellingWords: [],
    microsoftMisspellingWords: [],
    primaryCtaLink: null,
    microsoftCtaText: '',
    useRichTextEditor: false,
  };

  componentDidMount() {
    this.getStateFromProps();
  }

  getStateFromProps = async () => {
    const { templateId } = this.props;
    // Adding template
    if (!templateId || templateId <= 0) return;
    try {
      const template = await queryClient.fetchQuery({
        queryFn: () => getTemplateById(templateId),
        queryKey: ['templateDetail', { templateId }],
      });
      if (template.status === 'INACTIVE') {
        Modal.error({
          title: `Template ${templateId} is inactive`,
          content: 'Please reactivate the template to edit it.',
        });
        this.props.onHideModal();
        return;
      }
      const {
        name = '',
        subject = '',
        forceChoose = '',
        template: content = '',
        microsoftTemplate: microsoftContent = '',
        isAmp = 0,
        hasMicrosoft = 0,
        ampTemplate: ampHtml = '',
        textTemplate: textContent = '',
        ampReplaceMagicLink = false,
        isRateLimitExcluded,
        bypassUnsubscription,
        primaryCtaLink,
        microsoftCtaText = 'Submit',
        enablePauseLink,
      } = template;

      let { categoryId: category = -1, emailTypeId = -1 } = template;

      category = category < 1 ? -1 : category;

      this.setState({
        name,
        subject,
        selectedCategoryId: category,
        selectedEmailTypeId: emailTypeId,
        forceChoose,
        htmlContent: beautifyHtml(content),
        microsoftHtml: beautifyHtml(microsoftContent),
        ampEnabled: !!isAmp,
        microsoftEnabled: !!hasMicrosoft,
        ampHtml: beautifyHtml(ampHtml),
        textContent,
        ampReplaceMagicLink: !!ampReplaceMagicLink,
        isRateLimitExcluded: template.id ? !!isRateLimitExcluded : null, // new templates should have is_rate_limit_excluded as null
        linterErrors: {},
        errors: {},
        bypassUnsubscription: !!bypassUnsubscription,
        template,
        primaryCtaLink,
        microsoftCtaText,
        enablePauseLink,
      });
    } catch (e) {}
  };

  clearError = key => ({ ...this.state.errors, [key]: '' });

  handleHtmlTextAreaChange = htmlContent => {
    const errors = this.clearError('htmlContent');
    this.setState({ htmlContent, errors });
  };

  handleMicrosoftHtmlTextAreaChange = microsoftHtml => {
    const errors = this.clearError('microsoftHtml');
    this.setState({ microsoftHtml, errors });
  };

  handleAmpTextAreaChange = ampHtml => {
    const errors = this.clearError('ampHtml');
    this.setState({ ampHtml, errors });
  };

  handleTextAreaChange = event => {
    const { value: textContent } = event.target;
    const errors = this.clearError('textContent');
    this.setState({ textContent, errors });
  };

  handleInputChange = event => {
    const { target } = event;
    const { value, name } = target;
    const errors = this.clearError(name);
    this.setState({ [name]: value, errors });
  };

  handleForceChooseChange = forceChoose => {
    this.setState({ forceChoose });
  };

  handlePrimaryCtaLinkChange = primaryCtaLink => {
    this.setState({ primaryCtaLink: primaryCtaLink || null });
  };

  handleCategoryChange = value => {
    const { errors } = this.state;
    errors.selectedCategoryId = '';
    this.setState({
      selectedCategoryId: value,
      errors: { ...errors },
    });
  };

  handleEmailTypeChange = value => {
    const { errors } = this.state;
    errors.selectedEmailTypeId = '';
    this.setState({
      selectedEmailTypeId: value,
      errors: { ...errors },
    });
  };

  handleKeyUp = event => {
    const enterKeyNumber = 13;
    if (event.which === enterKeyNumber) {
      this.handleSaveTemplate();
    }
  };

  handleTabChange = activeTabKey => {
    this.setState({ activeTabKey });
  };

  handleSaveTemplate = async () => {
    const id = this.props.templateId;
    let {
      name,
      subject,
      selectedCategoryId: category_id,
      htmlContent: template,
      ampHtml: amp_template,
      microsoftHtml: microsoft_template,
      textContent: text_template,
      forceChoose,
      ampEnabled,
      microsoftEnabled,
      ampReplaceMagicLink,
      isRateLimitExcluded,
      selectedEmailTypeId: email_type_id,
      bypassUnsubscription: bypass_unsubscription,
      primaryCtaLink,
      microsoftCtaText,
      enablePauseLink,
    } = this.state;
    if (!(await this.validateValues())) {
      return;
    }

    const shouldBlockSavingTemplateWithInvalidVariable = getEnv(
      'REACT_APP_BLOCK_SAVING_TEMPLATE_WITH_INVALID_VARIABLE',
      {
        isBoolean: true,
      },
    );

    if (shouldBlockSavingTemplateWithInvalidVariable) {
      const invalidTemplateVariable = scanForInvalidVariables(
        [subject, template, text_template, microsoft_template],
        [amp_template],
      );

      if (invalidTemplateVariable) {
        return Modal.error({
          title: 'HTML Contains Invalid Variables',
          content: (
            <InvalidTemplateVariableWarning
              invalidVariable={invalidTemplateVariable}
            />
          ),
        });
      }
    }

    if (doesHtmlContainAmp(template)) {
      return Modal.error({
        title: 'HTML Contains Errors',
        content: 'HTML should not contain AMP codes',
      });
    }

    if (ampEnabled && !text_template) {
      text_template = template;
    }

    if (email_type_id === -1 || this.props.emailTypes.length <= 0) {
      // If user not allowed to set email type, or user hasn't select any type, clear this value
      // backend will save it using the default value
      email_type_id = undefined;
    }

    // HTML Validness Warning Stuff:
    // TODO: Remove this stuff when @vikram or @zech told so
    const htmlContainErrors = await Promise.all([
      getHtmlValidationResult(template),
      ampEnabled ? getHtmlValidationResult(amp_template) : false,
      microsoftEnabled ? getHtmlValidationResult(microsoft_template) : false,
    ]);

    const saveTemplate = () => {
      this.props.onSaveTemplate({
        name,
        subject,
        category_id,
        template: minify(template),
        amp_template: minify(amp_template),
        microsoft_template: minify(microsoft_template),
        text_template,
        id: id > 0 ? id : undefined,
        force_choose: forceChoose,
        is_amp: ampEnabled,
        has_microsoft: ampEnabled && microsoftEnabled,
        amp_replace_magic_link: !!ampReplaceMagicLink,
        is_rate_limit_excluded: isRateLimitExcluded,
        override_html_checks: true, // TODO: Remove this when @vikram or @zech told so
        email_type_id,
        bypass_unsubscription,
        primary_cta_link: primaryCtaLink,
        microsoft_cta_text: microsoftCtaText,
        enable_pause_link: enablePauseLink,
      });
      this.handleHideModal();
    };

    const [htmlErrors, ampHtmlErrors, microsoftHtmlErrors] = htmlContainErrors;

    if (
      htmlErrors?.errorCount ||
      ampHtmlErrors?.errorCount ||
      microsoftHtmlErrors?.errorCount ||
      this.state.misspellingWords.length > 0 ||
      this.state.microsoftMisspellingWords.length > 0 ||
      this.state.ampMisspellingWords > 0
    ) {
      Modal.confirm({
        title: 'The template contains some errors',
        content: (
          <TemplateErrorModal
            ampHtmlErrors={ampHtmlErrors?.errorCount}
            ampSpellingErrors={this.state.ampMisspellingWords.length}
            hasMicrosoft={microsoftEnabled}
            htmlErrors={htmlErrors?.errorCount}
            isAmp={ampEnabled}
            microsoftHtmlErrors={microsoftHtmlErrors?.errorCount}
            microsoftSpellingErrors={
              this.state.microsoftMisspellingWords.length
            }
            spellingErrors={this.state.misspellingWords.length}
          />
        ),
        // 'Do you want to still save it? It may cause different email experiences among the receivers.',
        okText: 'Just Save',
        onOk: saveTemplate,
        onCancel: () => {},
      });
      return;
    }
    saveTemplate();
  };

  validateValues = async () => {
    const errors = {};

    // eslint-disable-next-line no-unused-vars
    for (const key in validators) {
      if (Object.hasOwnProperty.call(validators, key)) {
        const validator = validators[key];
        const value = this.state[key];
        errors[key] = await getError(value, validator);
      }
    }

    if (this.state.ampEnabled) {
      errors['ampHtml'] = await getError(this.state.ampHtml, [
        required,
        minLength(6),
        // isHtmlValid,
      ]);
    }

    if (this.state.microsoftEnabled) {
      errors['microsoftHtml'] = await getError(this.state.microsoftHtml, [
        required,
        minLength(6),
        // isHtmlValid,
      ]);
    }

    const hasError = find(errors, v => !!v);
    if (hasError) this.setState({ errors });
    return !hasError;
  };

  handleHideModal = () => {
    this.props.onHideModal();
  };

  resetForm = () => {
    this.setState({
      name: '',
      subject: '',
      htmlContent: '',
      selectedCategoryId: -1,
      selectedEmailTypeId: -1,
      template: null,
    });
  };

  handleAmpReplaceMagicLinkEnableChange = () => {
    this.setState({
      ampReplaceMagicLink: !this.state.ampReplaceMagicLink,
    });
  };

  handleAmpChange = () => {
    this.setState({
      ampEnabled: !this.state.ampEnabled,
    });
  };

  handleMicrosoftChange = () => {
    this.setState({
      microsoftEnabled: !this.state.microsoftEnabled,
    });
  };

  onChangeRateLimitExcluded = checked => {
    this.setState({
      isRateLimitExcluded: !!checked,
    });
  };

  handleUseRichTextEditorChange = checked => {
    this.setState({
      useRichTextEditor: !!checked,
    });
  };

  render() {
    const { isVisible, categories, emailTypes, templateId } = this.props;
    const {
      name,
      subject,
      htmlContent,
      microsoftHtml,
      ampHtml,
      textContent,
      activeTabKey,
      errors,
      selectedCategoryId,
      selectedEmailTypeId,
      forceChoose,
      ampEnabled,
      microsoftEnabled,
      isRateLimitExcluded,
      bypassUnsubscription,
      ampReplaceMagicLink,
      primaryCtaLink,
      microsoftCtaText,
      enablePauseLink,
      useRichTextEditor,
    } = this.state;

    return (
      <Drawer
        className="email-template-editor"
        keyboard={false}
        title={`Edit Template ${templateId ? `(${templateId})` : ''}`} // Do not close window by accident press
        visible={isVisible}
        width="90vw"
        onClose={this.handleHideModal}
      >
        <Row gutter={8}>
          <Col span={10}>
            <Input
              error={errors.name}
              helperTxt={errors.name}
              id="email-template-name"
              label="Email template name"
              name="name"
              placeholder="Email subject"
              value={name}
              onChange={this.handleInputChange}
              onKeyUp={this.handleKeyUp}
            />
          </Col>
          <Col span={4}>
            <h6 className="category-head"> Category </h6>
            <Select
              error={errors.selectedCategoryId}
              helperTxt={errors.selectedCategoryId}
              id="email-category-select"
              name="selectedCategoryId"
              value={selectedCategoryId}
              onChange={this.handleCategoryChange}
            >
              <SelectOption disabled key="default" value={-1}>
                Select category
              </SelectOption>
              {categories.map((category, index) => (
                <SelectOption
                  id={`email-category-option-${index}`}
                  key={category.id}
                  value={category.id}
                >
                  {category.name}
                </SelectOption>
              ))}
            </Select>
          </Col>
          {emailTypes.length > 0 && (
            <Col span={4}>
              <SelectTemplateType
                emailTypes={emailTypes}
                enableRestriction={
                  this.state.template?.emailTypeId ===
                  EMAIL_TYPE_IDS.TRANSACTIONAL
                }
                error={errors.selectedEmailTypeId}
                value={selectedEmailTypeId}
                onChange={this.handleEmailTypeChange}
              />
            </Col>
          )}
          <Col span={4}>
            <div
              className="flex w-full space-x-2 items-center"
              style={{ marginBottom: 8 }}
            >
              <h6 className="input-name" style={{ marginBottom: 0 }}>
                Send Through
              </h6>
              <Tooltip title="This template will be forced to be sent through selected Email Service Provider if specified">
                <Icon theme="twoTone" type="info-circle" />
              </Tooltip>
            </div>
            <Select
              error=""
              helperTxt=""
              value={forceChoose}
              onChange={this.handleForceChooseChange}
            >
              <SelectOption key="default" value={null}>
                Automated Load Balancing
              </SelectOption>
              {SEND_THROUGH_LIST.map(sendThrough => (
                <SelectOption key={sendThrough} value={sendThrough}>
                  {sendThrough}
                </SelectOption>
              ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col span={16}>
            <Input
              error={errors.subject}
              helperTxt={errors.subject}
              id="email-template-subject"
              label="Email subject"
              name="subject"
              placeholder="Email subject"
              value={subject}
              onChange={this.handleInputChange}
              onKeyUp={this.handleKeyUp}
            />
          </Col>
          <Col offset={1} span={4} style={{ marginTop: '22px' }}>
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Checkbox checked={ampEnabled} onChange={this.handleAmpChange}>
                Enable AMP HTML
              </Checkbox>
              <Checkbox
                checked={microsoftEnabled}
                disabled={!ampEnabled}
                onChange={this.handleMicrosoftChange}
              >
                Enable Microsoft HTML
              </Checkbox>
              <RateLimitCheckBox
                checked={isRateLimitExcluded}
                setChecked={this.onChangeRateLimitExcluded}
              />
              <BypassUnsubscriptionCheckbox
                checked={bypassUnsubscription}
                setChecked={checked =>
                  this.setState({ bypassUnsubscription: checked })
                }
              />
              {selectedEmailTypeId === EMAIL_TYPE_IDS.OUTBOUND && (
                <Checkbox
                  checked={enablePauseLink}
                  onChange={() => {
                    this.setState({ enablePauseLink: !enablePauseLink });
                  }}
                >
                  Enable Pause Link
                </Checkbox>
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <Col lg={11} md={24} sm={24} span={ampEnabled ? 11 : 24} xs={24}>
            <Tabs activeKey={activeTabKey} onChange={this.handleTabChange}>
              <TabPane key="htmlEditor" tab={ampEnabled ? 'HTML' : 'Editor'}>
                {useRichTextEditor ? (
                  <RichTextEditor
                    placeholder="Write your email here"
                    value={htmlContent}
                    onChange={this.handleHtmlTextAreaChange}
                  />
                ) : (
                  <CodeEditor
                    error={errors.htmlContent}
                    helperTxt={errors.htmlContent}
                    language="html"
                    placeholder="Write your email here"
                    value={htmlContent}
                    onChange={this.handleHtmlTextAreaChange}
                    onMisspellings={misspellingWords =>
                      this.setState({ misspellingWords })
                    }
                  />
                )}

                <Checkbox
                  checked={useRichTextEditor}
                  disabled={/(<style>|<button[^>]*>|<table[^>]*>)/g.test(
                    htmlContent,
                  )}
                  style={{ marginTop: '.25rem' }}
                  onChange={e =>
                    this.handleUseRichTextEditorChange(e.target.checked)
                  }
                >
                  Use Rich Text Editor
                  {/<[^>]*style[^>]*>/g.test(htmlContent) &&
                    ' - Warning: style attributes will be removed in RTE!'}
                </Checkbox>
              </TabPane>
              {ampEnabled ? (
                <TabPane key="ampHtml" tab="AMP HTML">
                  <CodeEditor
                    error={errors.ampHtml}
                    helperTxt={errors.ampHtml}
                    language="html"
                    placeholder="Write your email here"
                    value={ampHtml}
                    onChange={this.handleAmpTextAreaChange}
                    onMisspellings={ampMisspellingWords =>
                      this.setState({ ampMisspellingWords })
                    }
                  />
                  <Checkbox
                    checked={ampReplaceMagicLink}
                    onChange={this.handleAmpReplaceMagicLinkEnableChange}
                  >
                    Replace <code>%token%</code> with Magic Link Token
                  </Checkbox>
                </TabPane>
              ) : null}

              {ampEnabled && microsoftEnabled ? (
                <TabPane key="microsoftHtml" tab="Microsoft HTML">
                  {useRichTextEditor ? (
                    <RichTextEditor
                      placeholder="Write your microsoft email here"
                      value={microsoftHtml}
                      onChange={this.handleMicrosoftHtmlTextAreaChange}
                    />
                  ) : (
                    <CodeEditor
                      error={errors.microsoftHtmlContent}
                      helperTxt={errors.microsoftHtmlContent}
                      language="html"
                      placeholder="Write your microsoft email here"
                      value={microsoftHtml}
                      onChange={this.handleMicrosoftHtmlTextAreaChange}
                      onMisspellings={microsoftMisspellingWords =>
                        this.setState({ microsoftMisspellingWords })
                      }
                    />
                  )}
                </TabPane>
              ) : null}

              <TabPane key="textEditor" tab="Text">
                <TextArea
                  className="editor"
                  error={errors.textContent}
                  helperTxt={errors.textContent}
                  id="email-template-body"
                  name="textContent"
                  placeholder="Write your email here"
                  rows={5}
                  value={textContent}
                  onChange={this.handleTextAreaChange}
                />
              </TabPane>
            </Tabs>
          </Col>
          <Col
            lg={11}
            md={24}
            sm={24}
            span={11}
            style={{ marginLeft: '30px' }}
            xs={24}
          >
            <Tabs activeKey={activeTabKey} onChange={this.handleTabChange}>
              <TabPane key="htmlEditor" tab="Preview">
                <RenderHTML
                  bypassUnsubscription={bypassUnsubscription}
                  className="preview"
                  html={htmlContent}
                  misspellingWords={this.state.misspellingWords}
                />
              </TabPane>
              {ampEnabled && (
                <TabPane key="ampHtml" tab="Preview AMP">
                  <RenderHTML
                    bypassUnsubscription={bypassUnsubscription}
                    className="preview"
                    html={ampHtml}
                    misspellingWords={this.state.ampMisspellingWords}
                  />
                </TabPane>
              )}
              {ampEnabled && microsoftEnabled && (
                <TabPane key="microsoftHtml" tab="Preview Microsoft">
                  <RenderHTML
                    bypassUnsubscription={bypassUnsubscription}
                    className="preview"
                    html={getMicrosoftCtaPreview(
                      microsoftHtml,
                      microsoftCtaText,
                    )}
                    misspellingWords={this.state.microsoftMisspellingWords}
                  />
                </TabPane>
              )}
            </Tabs>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 15,
            marginBottom: 15,
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <div style={{ display: 'flex' }}>
              <PrimaryCTALinkSelector
                ampHtml={ampHtml}
                html={htmlContent}
                value={primaryCtaLink || ''}
                onChange={this.handlePrimaryCtaLinkChange}
              />

              <div style={{ width: '100%', marginTop: 5 }}>
                {ampEnabled && microsoftEnabled && (
                  <Input
                    error={errors.name}
                    helperTxt={errors.name}
                    id="email-template-microsoftCtaText"
                    label="Microsoft CTA Text"
                    name="microsoftCtaText"
                    placeholder="Enter text to be displayed on the submit button of microsoft html"
                    style={{ width: '90%' }}
                    value={microsoftCtaText}
                    onChange={this.handleInputChange}
                    onKeyUp={this.handleKeyUp}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            className="email-template-editor_submit-btns"
            style={{ minWidth: 300, marginTop: 10 }}
          >
            <Button onClick={this.handleHideModal}>Cancel</Button>
            <Button
              id="save-template-btn"
              type="primary"
              onClick={this.handleSaveTemplate}
            >
              Save
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
}

EmailEditor.defaultProps = {
  template: {},
  categories: [],
  emailTypes: [],
  onSaveTemplate: noop,
};

EmailEditor.propTypes = {
  categories: PropTypes.array,
  emailTypes: PropTypes.array,
  isVisible: PropTypes.bool.isRequired,
  templateId: PropTypes.number,
  onHideModal: PropTypes.func.isRequired,
  onSaveTemplate: PropTypes.func,
};

export default EmailEditor;
